<template>
  <aside class="agent-threads">
    <div class="agent-threads__header">
      <span class="agent-threads__heading">Conversations</span>
      <button type="button" class="agent-threads__new" :disabled="disabled" @click="emit('new')">
        <Icon icon="mdi:plus" />
        <span>New chat</span>
      </button>
    </div>

    <div v-if="conversations.length > 0" class="agent-threads__search">
      <Input
        v-model="searchQuery"
        size="sm"
        placeholder="Search conversations"
        leading-icon="mdi:magnify"
        clearable
        aria-label="Search past conversations"
        @keydown.esc="searchQuery = ''"
      />
    </div>

    <div class="agent-threads__list">
      <div v-if="list.isLoading.value" class="agent-threads__state">
        <Spinner />
      </div>
      <p v-else-if="list.isError.value" class="agent-threads__state">
        Couldn't load your conversations.
        <button type="button" class="agent-threads__retry" @click="list.refetch()">Retry</button>
      </p>
      <p v-else-if="conversations.length === 0" class="agent-threads__state">
        Your past conversations will show up here.
      </p>
      <p v-else-if="filteredConversations.length === 0" class="agent-threads__state">
        No conversations match “{{ searchQuery.trim() }}”.
        <button type="button" class="agent-threads__retry" @click="searchQuery = ''">
          Clear search
        </button>
      </p>

      <div
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        class="agent-threads__item"
        :class="{
          'agent-threads__item--active': conversation.id === activeId,
          'agent-threads__item--disabled': disabled,
          'agent-threads__item--editing': conversation.id === editingId,
          'agent-threads__item--unread': conversation.unread,
        }"
      >
        <div v-if="conversation.id === editingId" class="agent-threads__edit">
          <input
            :ref="focusRenameInput"
            v-model="editingTitle"
            class="agent-threads__edit-input"
            maxlength="200"
            aria-label="Search name"
            @keydown.enter.prevent="saveRename"
            @keydown.esc.prevent="cancelRename"
            @blur="saveRename"
          />
          <span class="agent-threads__edit-hint">Enter to save · Esc to cancel</span>
        </div>
        <button
          v-else
          type="button"
          class="agent-threads__open"
          :disabled="disabled"
          :title="conversation.title || untitled"
          @click="emit('select', conversation.id)"
        >
          <span class="agent-threads__title">
            <Icon
              v-if="conversation.mode === 'assistant'"
              icon="mdi:creation"
              class="agent-threads__mode"
              aria-label="CRM assistant"
            />
            <span class="agent-threads__title-text">{{ conversation.title || untitled }}</span>
            <span
              v-if="conversation.unread"
              class="agent-threads__unread-dot"
              role="img"
              aria-label="Unread"
            />
          </span>
          <!-- An AI reply draft the rep hasn't opened: the assistant's message is waiting. -->
          <span
            v-if="conversation.unread"
            class="agent-threads__status agent-threads__status--reply"
          >
            <Icon icon="mdi:email-fast-outline" />
            New reply · draft ready · {{ formatDate(conversation.updatedAt) }}
          </span>
          <span
            v-else-if="enrichmentStatus?.[conversation.id] === 'enriching'"
            class="agent-threads__status agent-threads__status--enriching"
          >
            <span class="agent-threads__spinner" aria-hidden="true" />
            Enriching contacts…
          </span>
          <span
            v-else-if="enrichmentStatus?.[conversation.id] === 'completed'"
            class="agent-threads__status agent-threads__status--completed"
          >
            <Icon icon="mdi:check-circle" />
            Enrichment completed
          </span>
          <span v-else class="agent-threads__date">{{ formatDate(conversation.updatedAt) }}</span>
        </button>
        <div
          v-if="conversation.id !== editingId"
          class="agent-threads__menu"
          :aria-label="`Options for ${conversation.title || untitled}`"
        >
          <PopoverMenu
            :items="menuItems(conversation)"
            trigger-icon="mdi:dots-vertical"
            placement="bottom-right"
          />
        </div>
      </div>
    </div>

    <ActionConfirmationModal
      :show="!!toDelete"
      title="Delete conversation"
      :message="`Delete &quot;${toDelete?.title || untitled}&quot;? Contacts already imported from it are kept.`"
      confirm-text="Delete"
      cancel-text="Cancel"
      :is-loading="remove.isPending.value"
      @confirm="confirmDelete"
      @close="toDelete = null"
    />
  </aside>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Icon, Input, PopoverMenu, Spinner } from "@/components/ui";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import { useAgentConversations } from "@/composables/agent/useAgentConversations";
import { useToast } from "@/composables/ui/useToast";
import type { AgentChatMode, AgentConversationSummary } from "@/dtos/Agent.dto";
import type { AgentEnrichmentStatus } from "@/composables/agent/useAgentEnrichmentTracker";

const props = defineProps<{
  activeId: string | null;
  /** True while a turn is running — switching threads then would misplace the reply. */
  disabled?: boolean;
  /** Searches whose contacts are being enriched, or just finished. */
  enrichmentStatus?: Record<string, AgentEnrichmentStatus>;
  /** Only list threads of this kind; threads saved without a mode are prospecting. */
  mode?: AgentChatMode;
}>();

const emit = defineEmits<{
  select: [id: string];
  new: [];
  deleted: [id: string];
}>();

const untitled = "Untitled conversation";

const toast = useToast();
const { list, remove, rename } = useAgentConversations();

const conversations = computed<AgentConversationSummary[]>(() => {
  const all = list.data.value ?? [];
  if (!props.mode) return all;
  return all.filter((conversation) => (conversation.mode ?? "prospecting") === props.mode);
});
const toDelete = ref<AgentConversationSummary | null>(null);

// Filters the loaded searches by title; every typed word has to appear, in any order.
const searchQuery = ref("");
const filteredConversations = computed(() => {
  const words = searchQuery.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return conversations.value;
  return conversations.value.filter((conversation) => {
    const title = (conversation.title || untitled).toLowerCase();
    return words.every((word) => title.includes(word));
  });
});

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const menuItems = (conversation: AgentConversationSummary) => [
  {
    label: "Rename",
    icon: "mdi:pencil-outline",
    disabled: props.disabled,
    onClick: () => startRename(conversation),
  },
  {
    label: "Delete",
    icon: "mdi:delete-outline",
    danger: true,
    disabled: props.disabled,
    onClick: () => {
      toDelete.value = conversation;
    },
  },
];

// Inline rename: the title turns into a text field in place.
const editingId = ref<string | null>(null);
const editingTitle = ref("");

const startRename = (conversation: AgentConversationSummary) => {
  editingId.value = conversation.id;
  editingTitle.value = conversation.title || "";
};

const focusRenameInput = (el: unknown) => {
  if (el instanceof HTMLInputElement && document.activeElement !== el) {
    el.focus();
    el.select();
  }
};

const cancelRename = () => {
  editingId.value = null;
  editingTitle.value = "";
};

const saveRename = () => {
  const id = editingId.value;
  if (!id) return;
  const title = editingTitle.value.trim();
  const current = conversations.value.find((conversation) => conversation.id === id);
  // Closing the field first stops the blur that follows Enter from saving twice.
  cancelRename();
  if (!title || title === current?.title) return;
  rename.mutate(
    { id, title },
    { onError: () => toast.error("Couldn't rename this conversation. Please try again.") }
  );
};

const confirmDelete = () => {
  const target = toDelete.value;
  if (!target) return;
  remove.mutate(target.id, {
    onSuccess: (id) => {
      toDelete.value = null;
      emit("deleted", id);
    },
    onError: () => toast.error("Couldn't delete this conversation. Please try again."),
  });
};
</script>

<style scoped>
.agent-threads {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 260px;
  min-height: 0;
  border-right: 1px solid #e5e7eb;
  background: #fafafa;
}

.agent-threads__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 12px 10px 16px;
}

.agent-threads__heading {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.agent-threads__new {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #ddd6fe;
  border-radius: 8px;
  background: #fff;
  color: #6d28d9;
  font-size: 13px;
  cursor: pointer;
}

.agent-threads__new:hover:not(:disabled) {
  background: #f5f3ff;
}

.agent-threads__new:disabled {
  opacity: 0.5;
  cursor: default;
}

.agent-threads__search {
  padding: 0 12px 10px;
}

.agent-threads__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 12px;
}

.agent-threads__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 24px 12px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
}

.agent-threads__retry {
  border: none;
  background: none;
  color: #6d28d9;
  font-size: 13px;
  cursor: pointer;
}

.agent-threads__item {
  display: flex;
  align-items: center;
  border-radius: 8px;
}

.agent-threads__item:hover {
  background: #f3f4f6;
}

.agent-threads__item--active,
.agent-threads__item--active:hover {
  background: #ede9fe;
}

.agent-threads__item--disabled {
  opacity: 0.6;
}

.agent-threads__open {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 8px 4px 8px 10px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.agent-threads__open:disabled {
  cursor: default;
}

.agent-threads__title {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  color: #111827;
  font-size: 13px;
}

.agent-threads__title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-threads__mode {
  flex-shrink: 0;
  margin-right: 2px;
  color: #7c3aed;
}

/* Unread AI reply draft: bold, with a dot, like an unread message. */
.agent-threads__item--unread .agent-threads__title {
  font-weight: 600;
}

.agent-threads__unread-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-left: auto;
  border-radius: 50%;
  background: #7c3aed;
  box-shadow: 0 0 0 3px rgb(124 58 237 / 15%);
}

.agent-threads__status--reply {
  color: #6d28d9;
}

.agent-threads__status--reply .iconify {
  font-size: 13px;
}

.agent-threads__date {
  color: #9ca3af;
  font-size: 11px;
}

.agent-threads__status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
}

.agent-threads__status--enriching {
  color: #4f46e5;
}

.agent-threads__status--completed {
  color: #059669;
  animation: agent-threads-pop 0.3s ease-out;
}

.agent-threads__status--completed .iconify {
  font-size: 13px;
}

.agent-threads__spinner {
  width: 10px;
  height: 10px;
  border: 2px solid #c7d2fe;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: agent-threads-spin 0.8s linear infinite;
}

@keyframes agent-threads-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes agent-threads-pop {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .agent-threads__spinner,
  .agent-threads__status--completed {
    animation: none;
  }
}

.agent-threads__menu {
  flex-shrink: 0;
  margin-right: 4px;
  opacity: 0;
}

.agent-threads__menu :deep(.popover-menu__trigger) {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: #9ca3af;
  font-size: 16px;
}

.agent-threads__menu :deep(.popover-menu__trigger:hover) {
  background: #e5e7eb;
  color: #374151;
}

.agent-threads__item:hover .agent-threads__menu,
.agent-threads__item--active .agent-threads__menu,
.agent-threads__menu:focus-within {
  opacity: 1;
}

/* No hover on touch screens, so keep the menu button visible there. */
@media (hover: none) {
  .agent-threads__menu {
    opacity: 1;
  }
}

.agent-threads__item--editing,
.agent-threads__item--editing:hover {
  background: #fff;
  box-shadow: inset 0 0 0 1px #c4b5fd;
}

.agent-threads__edit {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 6px 8px;
}

.agent-threads__edit-input {
  width: 100%;
  padding: 3px 6px;
  border: 1px solid #a78bfa;
  border-radius: 6px;
  outline: none;
  color: #111827;
  font-size: 13px;
}

.agent-threads__edit-input:focus {
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
}

.agent-threads__edit-hint {
  color: #9ca3af;
  font-size: 10px;
}
</style>
